/**
 * OwnerErrorsPanel — frontend crash reports list for the owner dashboard.
 *
 * Pulls from /api/errors/recent (owner-gated). For each row shows:
 *   - signature (first stack frame)
 *   - message
 *   - last-seen time + seen_count
 *   - url where the crash happened
 *   - expand for full stack + component stack + context
 *   - Resolve / Delete actions
 *
 * iter-155 — surfaces the captured errors so the owner can find recurring
 * bugs without users needing to paste anything.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Trash2, RefreshCw, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  } catch (e) { void e; return iso; }
}

export default function OwnerErrorsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/errors/recent`, {
        params: { limit: 100, include_resolved: includeResolved },
        withCredentials: true,
      });
      setReports(r.data?.reports || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load reports";
      toast.error("Couldn't load crash reports", { description: msg });
    } finally {
      setLoading(false);
    }
  }, [includeResolved]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const resolve = async (id) => {
    try {
      await axios.post(`${API}/errors/${id}/resolve`, {}, { withCredentials: true });
      setReports((rs) => rs.filter((r) => r.id !== id || includeResolved));
      toast.success("Report marked as resolved");
      if (includeResolved) fetchReports();
    } catch (e) {
      toast.error("Couldn't resolve", { description: e?.response?.data?.detail || e?.message });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this report permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/errors/${id}`, { withCredentials: true });
      setReports((rs) => rs.filter((r) => r.id !== id));
      toast.success("Report deleted");
    } catch (e) {
      toast.error("Couldn't delete", { description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <section data-testid="owner-section-errors" className="mt-10">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">Frontend crash reports</h2>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-white/40">
            Captured by the ErrorBoundary · auto-deduped over 5 min windows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-white/55">
            <input
              type="checkbox"
              data-testid="owner-errors-include-resolved"
              checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)}
              className="h-3 w-3 accent-fuchsia-400"
            />
            Show resolved
          </label>
          <button
            type="button"
            data-testid="owner-errors-refresh"
            onClick={fetchReports}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/70 transition hover:bg-white/[0.10] disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && reports.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center font-mono text-[10px] uppercase tracking-wider text-white/40">
          Loading…
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-emerald-400/20 bg-emerald-500/[0.04] p-8 text-center">
          <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-300/70" />
          <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-emerald-200/80">
            No outstanding crashes · all clear
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => {
            const isOpen = expandedId === r.id;
            return (
              <div
                key={r.id}
                data-testid={`owner-error-${r.id}`}
                className={`rounded-xl border bg-white/[0.02] p-3 transition ${r.resolved ? "border-emerald-400/15 opacity-60" : "border-rose-400/20"}`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${r.resolved ? "text-emerald-300/60" : "text-rose-300"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="break-words font-mono text-xs text-white/90" data-testid={`owner-error-msg-${r.id}`}>
                      {r.message}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-wider text-white/45">
                      <span>{relTime(r.last_seen)}</span>
                      {r.seen_count > 1 && (
                        <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-amber-200 ring-1 ring-amber-300/30">
                          {r.seen_count}× repeat
                        </span>
                      )}
                      {r.url && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-0.5 text-cyan-300 hover:text-cyan-200"
                        >
                          <ExternalLink className="h-2.5 w-2.5" />
                          {(() => { try { return new URL(r.url).pathname || "/"; } catch (e) { void e; return r.url.slice(0, 30); } })()}
                        </a>
                      )}
                      {r.user_agent && (
                        <span title={r.user_agent} className="truncate max-w-[200px]">
                          · {r.user_agent.match(/(Chrome|Firefox|Safari|Edge)\/\S+/)?.[0] || "browser"}
                        </span>
                      )}
                      {r.resolved && (
                        <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-emerald-200 ring-1 ring-emerald-300/30">resolved</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      type="button"
                      data-testid={`owner-error-toggle-${r.id}`}
                      onClick={() => setExpandedId(isOpen ? null : r.id)}
                      className="rounded-md p-1.5 text-white/55 transition hover:bg-white/[0.08] hover:text-white"
                      title={isOpen ? "Collapse" : "Expand stack trace"}
                    >
                      {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                    {!r.resolved && (
                      <button
                        type="button"
                        data-testid={`owner-error-resolve-${r.id}`}
                        onClick={() => resolve(r.id)}
                        className="rounded-md p-1.5 text-emerald-300/80 transition hover:bg-emerald-500/15 hover:text-emerald-100"
                        title="Mark as resolved"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`owner-error-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="rounded-md p-1.5 text-rose-300/80 transition hover:bg-rose-500/15 hover:text-rose-100"
                      title="Delete permanently"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-3 space-y-2 border-t border-white/[0.06] pt-3">
                    {r.stack && (
                      <details open className="rounded-md bg-black/30 p-2">
                        <summary className="cursor-pointer font-mono text-[9px] uppercase tracking-wider text-white/50">Stack</summary>
                        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-white/70">{r.stack}</pre>
                      </details>
                    )}
                    {r.component_stack && (
                      <details className="rounded-md bg-black/30 p-2">
                        <summary className="cursor-pointer font-mono text-[9px] uppercase tracking-wider text-white/50">Component stack</summary>
                        <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-white/55">{r.component_stack}</pre>
                      </details>
                    )}
                    {r.context && Object.keys(r.context).length > 0 && (
                      <details className="rounded-md bg-black/30 p-2">
                        <summary className="cursor-pointer font-mono text-[9px] uppercase tracking-wider text-white/50">Context</summary>
                        <pre className="mt-2 max-h-24 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-cyan-200/70">{JSON.stringify(r.context, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
